import { Component, inject, OnInit } from '@angular/core';
import { Auth, signInWithCustomToken } from '@angular/fire/auth';
import { Firestore, doc, setDoc, getDoc } from '@angular/fire/firestore';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { environment } from '../../../../../src/environments/environment';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit {
  private auth = inject(Auth);
  private firestore = inject(Firestore);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  isLoading = false;
  errorMessage: string | null = null;

  showEmailPrompt = false;
  emailInput = '';
  pendingUser: any = null;

  ngOnInit() {
    this.route.queryParams.subscribe(async params => {
      const token = params['token'];
      const logout = params['logout'] === 'true';
      if (token) {
        await this.handleAuthToken(token);
      } else {
        this.loginUsingAuthPortal(logout);
      }
    });
  }

  async handleAuthToken(token: string) {
    this.isLoading = true;
    this.errorMessage = null;

    try {
      const result = await signInWithCustomToken(this.auth, token);
      const user = result.user;

      if (user) {
        const idTokenResult = await user.getIdTokenResult();
        const tokenEmail = idTokenResult.claims['email'] as string;

        const userRef = doc(this.firestore, 'users', user.uid);
        const docSnap = await getDoc(userRef);

        const existingEmail = docSnap.exists() ? docSnap.data()['email'] : null;
        const finalEmail = (user.email || tokenEmail || existingEmail || localStorage.getItem('userEmail') || '').trim().toLowerCase();

        if (finalEmail) {
          await this.completeLogin(user, finalEmail, docSnap);
        } else {
          // Show email prompt popup
          this.pendingUser = user;
          this.showEmailPrompt = true;
          this.isLoading = false;
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      this.errorMessage = 'Authentication failed. Please try again.';
      this.isLoading = false;
    }
  }

  async completeLogin(user: any, email: string, docSnap: any) {
    this.isLoading = true;
    localStorage.setItem('userEmail', email);

    const now = new Date();
    const name = user.displayName || (email ? email.split('@')[0] : '');
    const updateData: any = {
      uid: user.uid,
      displayName: name,
      name: name,
      lastLogin: now,
      lastLoginAt: now,
      email: email
    };

    // Only assign 'User' role if the user is new or doesn't have a role yet
    if (!docSnap.exists() || !docSnap.data()['role']) {
      updateData.registeredAt = docSnap.exists() ? (docSnap.data()['registeredAt'] || now) : now;
      let assignedRole = 'User';
      let assignedRoleId: string | undefined = undefined;
      try {
        const { collection, getDocs, query, where, limit } = await import('@angular/fire/firestore');
        const rolesRef = collection(this.firestore, 'roles');
        const q = query(rolesRef, where('name', 'in', ['User', 'user', 'USER']), limit(1));
        const rolesSnap = await getDocs(q);
        if (!rolesSnap.empty) {
          const userRoleDoc = rolesSnap.docs[0];
          assignedRole = userRoleDoc.data()['name'] || 'User';
          assignedRoleId = userRoleDoc.id;
        }
      } catch (e) {
        console.error('Error fetching user role:', e);
      }

      updateData.role = assignedRole;
      if (assignedRoleId) {
        updateData.roleId = assignedRoleId;
      }
    }

    await setDoc(doc(this.firestore, 'users', user.uid), updateData, { merge: true });

    // Redirect to requested URL or portal landing page
    const redirectUrl = sessionStorage.getItem('redirectUrl');
    if (redirectUrl) {
      sessionStorage.removeItem('redirectUrl');
      if (redirectUrl.startsWith('/session/')) {
        const [pathPart, queryPart] = redirectUrl.split('?');
        const sessionId = pathPart.split('/session/')[1];
        const queryParams = queryPart ? Object.fromEntries(new URLSearchParams(queryPart)) : {};
        this.router.navigate(['/home', sessionId], { queryParams });
      } else {
        this.router.navigateByUrl(redirectUrl);
      }
    } else {
      this.router.navigate(['/home']);
    }
  }

  async submitEmail() {
    if (!this.emailInput || !this.emailInput.trim()) {
      this.errorMessage = 'Please enter a valid email address.';
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(this.emailInput.trim())) {
      this.errorMessage = 'Please enter a valid email address format.';
      return;
    }

    if (this.pendingUser) {
      this.showEmailPrompt = false;
      this.errorMessage = null;
      try {
        const userRef = doc(this.firestore, 'users', this.pendingUser.uid);
        const docSnap = await getDoc(userRef);
        await this.completeLogin(this.pendingUser, this.emailInput.trim(), docSnap);
      } catch (err) {
        console.error('Error saving user profile with custom email:', err);
        this.errorMessage = 'Failed to save email. Please try again.';
      }
    }
  }

  loginUsingAuthPortal(logout = false) {
    this.isLoading = true;
    this.errorMessage = null;

    // We use the portal's origin for the callback URL
    const callbackUrl = window.location.origin + '/login';
    let portalUrl = `${environment.authPortalUrl}?redirect_url=${encodeURI(callbackUrl)}`;
    if (logout) {
      portalUrl += '&logout=true';
    }
    window.location.href = portalUrl;
  }
}
